import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FiCalendar, FiPackage } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { customersAPI, QBOrderDetail } from '../api/customers';
import { ordersAPI, OrderResponse } from '../api/orders';
import { fetchQuickBitesMenu } from '../api/bubbletea';
import { formatCurrency } from '../utils/currency';
import { getLocalizedField } from '../utils/localize';
import { localizeOptionsEntries } from '../utils/bubbleTeaOptionPricing';
import { getStatusLabel, formatQueueNumberValue } from '../utils/orderStatus';
import axiosInstance from '../api/axios';
import type { SupportedLanguage } from '../i18n';
import type { BubbleTeaOptionCategory } from '../api/adminBubbleTeaOptions';

const Container = styled.div`
  min-height: calc(100vh - var(--header-height));
  padding: 2rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
  margin-top: var(--header-height);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: var(--text-color);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  margin-top: 2rem;
  color: var(--text-color);
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OrderCard = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ClickableOrderCard = styled(OrderCard)`
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const OrderNumber = styled.h3`
  font-size: 1.125rem;
  color: var(--text-color);
`;

const OrderStatus = styled.span<{ $status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: ${props => {
    switch (props.$status) {
      case 'pending':
        return 'var(--warning-light, #FEF3C7)';
      case 'confirmed':
        return 'var(--info-light, #DBEAFE)';
      case 'ready':
        return 'var(--success-light, #E8F5E9)';
      case 'completed':
        return 'var(--success-color, #44BB44)';
      case 'preparing':
      case 'in_preparation':
        return 'var(--preparing-light, #FFE0B2)';
      case 'ready_for_pickup':
        return 'var(--success-light, #E8F5E9)';
      case 'cancelled':
        return 'var(--danger-light, #FFEBEE)';
      default:
        return 'var(--gray-light)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'pending':
        return 'var(--warning-dark, #D97706)';
      case 'confirmed':
        return 'var(--info-dark, #1E40AF)';
      case 'ready':
        return 'var(--success-dark, #2E7D32)';
      case 'completed':
        return 'var(--white)';
      case 'preparing':
      case 'in_preparation':
        return 'var(--preparing-dark, #E65100)';
      case 'ready_for_pickup':
        return 'var(--success-dark, #2E7D32)';
      case 'cancelled':
        return 'var(--danger-color, #C62828)';
      default:
        return 'var(--text-color)';
    }
  }};
`;

const OrderInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  color: var(--gray-dark);
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ItemsList = styled.ul`
  margin: 0;
  padding: 0 0 0 1rem;
  font-size: 0.875rem;
  color: var(--text-color);
`;

const ItemEntry = styled.li`
  margin-bottom: 0.25rem;
`;

const ItemOptions = styled.span`
  color: var(--gray-dark);
  font-size: 0.8rem;
`;

const EmptyState = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: var(--gray-dark);
`;

const LoadingState = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: var(--gray-dark);
`;

const AccountOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx / OrderDetail.tsx pattern: i18next's
  // language can be a region-qualified tag or unset, so narrow to the two
  // v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';
  const [qbOrders, setQbOrders] = useState<QBOrderDetail[]>([]);
  const [ccOrders, setCcOrders] = useState<OrderResponse[]>([]);
  const [qbLoading, setQbLoading] = useState(true);
  const [ccLoading, setCcLoading] = useState(true);
  // The QB order-history endpoint (/customers/me/orders) only ever returns a
  // flat, English-only `menu_item_name` per item, derived server-side with no
  // `_zh` sibling. Re-resolve each item's localized name against the live
  // quick-bites menu by menu_item_id, matching the established
  // QBCartDrawer.tsx/QBCheckout.tsx "live-lookup with English fallback"
  // pattern used for the same underlying data gap.
  const { data: qbMenu } = useQuery({ queryKey: ['quickBitesMenu'], queryFn: fetchQuickBitesMenu, enabled: isAuthenticated });

  // The QB order-history endpoint also only returns a flat, English-only
  // `options` map per item (categoryName -> value), with no `_zh` sibling.
  // Mirrors qbMenu just above: re-resolve each option's localized
  // category/item name against the live bubble tea option catalog, with
  // English fallback when a name has no live match (deleted/deactivated
  // since the order, or the category-name lookup itself misses).
  const { data: rawOptionCategories } = useQuery<BubbleTeaOptionCategory[]>({
    queryKey: ['bubbleTeaOptionCategories'],
    queryFn: async () => {
      // include_inactive=true: this is a logged-in customer's own order
      // history -- a deactivated category/item is still a valid thing to
      // look up and localize here (display-only), unlike live
      // ordering/pricing flows which must stay active-only. The endpoint
      // requires authentication to honor include_inactive (this query is
      // itself gated on isAuthenticated below, and axiosInstance already
      // attaches the Bearer token for logged-in requests).
      const res = await axiosInstance.get<BubbleTeaOptionCategory[]>('/quick-bites/options/categories', {
        params: { include_inactive: true },
      });
      return res.data;
    },
    enabled: isAuthenticated,
  });
  const optionCategories: BubbleTeaOptionCategory[] = Array.isArray(rawOptionCategories)
    ? rawOptionCategories
    : [];

  const getQbItemDisplayName = (item: { menu_item_id: number; menu_item_name: string; menu_item_name_zh?: string | null }): string => {
    const menuItem = qbMenu?.bubble_tea.find(bt => bt.menu_item_id === item.menu_item_id);
    if (menuItem) {
      return getLocalizedField(menuItem, 'name', language) as string;
    }
    // Live lookup missed (menu item deactivated/deleted since the order --
    // the routine path, since a hard delete is DB-blocked once an item has
    // order history). Fall back to the server-snapshotted zh name rather
    // than silently reverting to English for a zh-preferring customer.
    if (language === 'zh' && item.menu_item_name_zh && item.menu_item_name_zh.trim() !== '') {
      return item.menu_item_name_zh;
    }
    return item.menu_item_name;
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const fetchQbOrders = async () => {
      try {
        const orders = await customersAPI.getMyQBOrders();
        setQbOrders(orders);
      } catch (error) {
        console.error('Failed to fetch QB orders:', error);
      } finally {
        setQbLoading(false);
      }
    };

    const fetchCcOrders = async () => {
      try {
        const orders = await ordersAPI.getUserOrders();
        setCcOrders(orders);
      } catch (error) {
        console.error('Failed to fetch CC orders:', error);
        if ((error as any)?.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setCcLoading(false);
      }
    };

    Promise.all([fetchQbOrders(), fetchCcOrders()]);
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatOptions = (options: Record<string, string> | null): string => {
    if (!options) return '';
    return localizeOptionsEntries(options, optionCategories, language)
      .map(({ key, value }) => `${key}: ${value}`)
      .join(', ');
  };

  return (
    <Container>
      <Title>{t('accountOrders.title')}</Title>

      {/* QB Orders Section */}
      <SectionTitle>{t('accountOrders.qbSectionTitle')}</SectionTitle>
      {qbLoading ? (
        <LoadingState>{t('accountOrders.loading')}</LoadingState>
      ) : qbOrders.length === 0 ? (
        <EmptyState>
          <FiPackage size={32} style={{ marginBottom: '0.5rem', color: 'var(--gray-dark)' }} />
          <p>{t('accountOrders.qbEmpty')}</p>
        </EmptyState>
      ) : (
        <OrdersList>
          {qbOrders.map((order) => (
            <OrderCard key={order.id}>
              <OrderHeader>
                <OrderNumber>{t('accountOrders.queueNumberLabel', { number: formatQueueNumberValue(order.queue_number, t('accountOrders.notAvailable')) })}</OrderNumber>
                <OrderStatus $status={order.status.toLowerCase()}>
                  {getStatusLabel(order.status, t)}
                </OrderStatus>
              </OrderHeader>
              <OrderInfo>
                <InfoItem>
                  <FiCalendar />
                  <span>{formatDate(order.created_at)}</span>
                </InfoItem>
              </OrderInfo>
              <ItemsList>
                {order.items.map((item) => {
                  const optionsStr = formatOptions(item.options);
                  return (
                    <ItemEntry key={item.id}>
                      {item.quantity}x {getQbItemDisplayName(item)}
                      {optionsStr && (
                        <ItemOptions> ({optionsStr})</ItemOptions>
                      )}
                    </ItemEntry>
                  );
                })}
              </ItemsList>
            </OrderCard>
          ))}
        </OrdersList>
      )}

      {/* CC Orders Section */}
      <SectionTitle>{t('accountOrders.ccSectionTitle')}</SectionTitle>
      {ccLoading ? (
        <LoadingState>{t('accountOrders.loading')}</LoadingState>
      ) : ccOrders.length === 0 ? (
        <EmptyState>
          <FiPackage size={32} style={{ marginBottom: '0.5rem', color: 'var(--gray-dark)' }} />
          <p>{t('accountOrders.ccEmpty')}</p>
        </EmptyState>
      ) : (
        <OrdersList>
          {ccOrders.map((order) => (
            <ClickableOrderCard key={order.order_id} onClick={() => navigate(`/orders/${order.order_id}`)}>
              <OrderHeader>
                <OrderNumber>{t('accountOrders.orderNumberLabel', { number: order.order_number })}</OrderNumber>
                <OrderStatus $status={order.status.toLowerCase()}>
                  {getStatusLabel(order.status, t)}
                </OrderStatus>
              </OrderHeader>
              <OrderInfo>
                <InfoItem>
                  <FiCalendar />
                  <span>{t('accountOrders.pickupLabel', { date: formatDate(order.pickup_date) })}</span>
                </InfoItem>
                <InfoItem>
                  <span>{formatCurrency(order.total_amount)}</span>
                </InfoItem>
              </OrderInfo>
            </ClickableOrderCard>
          ))}
        </OrdersList>
      )}
    </Container>
  );
};

export default AccountOrdersPage;
